/**
 * Phase 19.1 — inline "Contact Starz" button row.
 *
 * Rendered below an assistant message whose `contact_starz` is true
 * (Stella emitted `[[CONTACT_STARZ]]`, or the pricing pipeline
 * returned `RequiresConsultation`). Two buttons:
 *
 *   - **Email Starz** → opens the user's mail client with a prefilled
 *     subject + body referencing the estimate.
 *   - **Call Starz** → opens the dialler (`tel:`). Disabled outside
 *     the tenant's business hours; tooltip shows the next open time.
 *
 * Phone-button enablement is computed CLIENT-side from the payload's
 * weekly hours + timezone — no extra fetch.
 */

import type { ContactStarzPayload } from "@starz/shared/api/types.gen";

interface Props {
  payload: ContactStarzPayload;
  estimateId: string;
  estimateUrl?: string; // optional admin link to include in the email body
}

interface OpenStatus {
  isOpen: boolean;
  nextOpensAt: string | null; // human-friendly next-open description
}

/**
 * Compute whether the tenant is open *right now* against their
 * timezone + weekly schedule. Returns the next-open description when
 * closed so the disabled-button tooltip can say "Opens Mon at 8:00 AM".
 */
function computeOpenStatus(
  weekly: ([number, number] | null)[] | null | undefined,
  timezone: string | null | undefined,
): OpenStatus {
  if (!weekly || weekly.length !== 7) {
    return { isOpen: true, nextOpensAt: null };
  }
  const tz = timezone || "UTC";
  // Pull the current weekday + hour in the tenant's timezone using
  // Intl.DateTimeFormat — avoids pulling in a date lib.
  const now = new Date();
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    hour: "numeric",
    hour12: false,
  });
  const parts = fmt.formatToParts(now);
  const weekdayShort = parts.find((p) => p.type === "weekday")?.value ?? "";
  const hourStr = parts.find((p) => p.type === "hour")?.value ?? "0";
  const weekdayIdx = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(
    weekdayShort,
  );
  const hour = Number(hourStr);
  if (weekdayIdx < 0 || Number.isNaN(hour)) {
    return { isOpen: true, nextOpensAt: null };
  }
  const today = weekly[weekdayIdx];
  if (today && hour >= today[0] && hour < today[1]) {
    return { isOpen: true, nextOpensAt: null };
  }
  // Walk forward up to 7 days to find the next opening.
  for (let offset = 0; offset < 8; offset++) {
    const checkIdx = (weekdayIdx + offset) % 7;
    const window = weekly[checkIdx];
    if (!window) continue;
    if (offset === 0 && hour < window[0]) {
      return {
        isOpen: false,
        nextOpensAt: `Opens today at ${formatHour(window[0])}`,
      };
    }
    if (offset > 0) {
      const dayName = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][checkIdx];
      return {
        isOpen: false,
        nextOpensAt: `Opens ${dayName} at ${formatHour(window[0])}`,
      };
    }
  }
  return { isOpen: false, nextOpensAt: null };
}

function formatHour(h: number): string {
  const ampm = h < 12 ? "AM" : "PM";
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hr}:00 ${ampm}`;
}

function buildMailto(
  email: string,
  estimateId: string,
  estimateUrl: string | undefined,
  reason: string | null | undefined,
): string {
  const subject = `Starz estimate help — ${estimateId}`;
  const bodyLines = [
    "Hi Starz team,",
    "",
    "I'm looking at a project that needs a technician to walk through with me.",
    reason ? `Project type: ${reason}` : "",
    `Estimate reference: ${estimateId}`,
    estimateUrl ? `Project link: ${estimateUrl}` : "",
    "",
    "Please let me know when we can connect.",
    "",
    "Thanks!",
  ].filter(Boolean);
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
}

export function ContactStarzButtons({ payload, estimateId, estimateUrl }: Props) {
  const { isOpen, nextOpensAt } = computeOpenStatus(
    payload.business_hours_weekly,
    payload.business_hours_timezone,
  );

  return (
    <div className="ml-1 mt-2 space-y-2">
      <div className="flex flex-wrap gap-2">
        {payload.support_email && (
          <a
            href={buildMailto(
              payload.support_email,
              estimateId,
              estimateUrl,
              payload.reason,
            )}
            className="inline-flex items-center gap-2 rounded-full bg-starz-navy px-4 py-2 text-sm font-semibold text-starz-off-white hover:opacity-90"
          >
            <svg
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            Email Starz
          </a>
        )}
        {payload.support_phone && (
          <a
            href={isOpen ? `tel:${payload.support_phone}` : undefined}
            aria-disabled={!isOpen}
            title={!isOpen && nextOpensAt ? nextOpensAt : undefined}
            className={
              isOpen
                ? "inline-flex items-center gap-2 rounded-full bg-starz-gold px-4 py-2 text-sm font-semibold text-starz-charcoal hover:opacity-90"
                : "inline-flex items-center gap-2 rounded-full bg-warm-gray/30 px-4 py-2 text-sm font-semibold text-warm-gray cursor-not-allowed"
            }
            onClick={(e) => {
              if (!isOpen) e.preventDefault();
            }}
          >
            <svg
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            Call Starz
          </a>
        )}
      </div>
      {!isOpen && nextOpensAt && (
        <p className="text-[11px] italic text-warm-gray">{nextOpensAt}</p>
      )}
    </div>
  );
}
